import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { erpApi } from '@/shared/axios/erp-api';
import {
  getUrlParamsFetch,
  notFoundIdHandler,
  urlBackend,
} from '@/shared';
import {
  AxiosCodeEnum,
  FacturacionElectronica,
  FacturacionElectronicaPagingRes,
  UseFetchEnabledParams,
  UseMutationsParams,
} from '@/shared/interfaces';

import { useUiStore } from '@/store/ui';

const { get, post, put, remove } = erpApi();

///* tanStack query
export const useFetchFacturacionElectronicas = ({
  enabled = true,
  params,
}: UseFetchEnabledParams<GetFacturacionElectronicasParams>) => {
  return useQuery({
    queryKey: ['facturacion-electronicas', ...Object.values(params ?? {})],
    queryFn: () => getFacturacionElectronicas(params),
    enabled: enabled,
  });
};

export const useGetFacturacionElectronica = (id: string) => {
  return useQuery({
    queryKey: ['facturacion-electronica', id],
    queryFn: () => getFacturacionElectronica(id),
    retry: false,
  });
};

export const useCreateFacturacionElectronica = ({
  navigate,
  returnUrl,
  enableRedirect = true,
}: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;

  return useMutation({
    mutationFn: createFacturacionElectronica,
    onSuccess: res => {
      queryClient.invalidateQueries({ queryKey: ['facturacion-electronicas'] });

      if (res.code === AxiosCodeEnum.CREATED) {
        enableRedirect && navigate && returnUrl && navigate(returnUrl);
      }
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useUpdateFacturacionElectronica = ({
  navigate,
  returnUrl,
  enableRedirect = true,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateFacturacionElectronica,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['facturacion-electronicas'] });
      enableRedirect && navigate && returnUrl && navigate(returnUrl);
    },
  });
};

export const useDeleteFacturacionElectronica = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteFacturacionElectronica,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['facturacion-electronicas'] });
    },
  });
};

///* axios
export type GetFacturacionElectronicasParams =
  Partial<FacturacionElectronica> & {
    page?: number;
    page_size?: number;
  };
export type CreateFacturacionElectronicaParams = Omit<
  FacturacionElectronica,
  'id_facturacion_electronica'
>;
export type UpdateFacturacionElectronicaParams = {
  id: string;
  data: CreateFacturacionElectronicaParams;
};

export const getFacturacionElectronicas = async (
  params?: GetFacturacionElectronicasParams
) => {
  const queryParams = getUrlParamsFetch(params ?? {});

  return get<FacturacionElectronicaPagingRes>(
    `${urlBackend}api/v1/facturacion-electronica/?${queryParams}`,
    true
  );
};

export const getFacturacionElectronica = async (id: string) => {
  const res = await get<FacturacionElectronica>(
    `${urlBackend}api/v1/facturacion-electronica/${id}`,
    true
  );

  notFoundIdHandler(res);
  return res;
};

export const createFacturacionElectronica = async (
  data: CreateFacturacionElectronicaParams
) => {
  const setAlert = useUiStore.getState().setAlert;
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;

  setIsGlobalLoading(true);
  const res = await post<FacturacionElectronica>(
    `${urlBackend}api/v1/facturacion-electronica/`,
    data,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};

export const updateFacturacionElectronica = async ({
  id,
  data,
}: UpdateFacturacionElectronicaParams) => {
  const setAlert = useUiStore.getState().setAlert;

  const res = await put<FacturacionElectronica>(
    `${urlBackend}api/v1/facturacion-electronica/${id}/`,
    data,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};

export const deleteFacturacionElectronica = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;

  const res = await remove<FacturacionElectronica>(
    `${urlBackend}api/v1/facturacion-electronica/${id}/`,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};
